import { defaultsDeep, flow } from 'lodash';
import { inject, provide, isRef, ref, isReadonly, watchEffect, computed, watch, getCurrentInstance, onMounted, onUnmounted, onBeforeUnmount, toRef } from 'vue-demi';
import { getWindow, getDocument, getHTMLElement, getNavigator, freeze, mayFunctionValue, isSSR, getStorage } from '@winter-love/utils';
import * as process$1 from 'process';
import { debounce } from 'debounce';
import { ref as ref$1, watch as watch$1 } from 'vue';
import stringify from 'fast-json-stable-stringify';

const createGetDataLayer = () => {
    let _init = false;
    return () => {
        const { dataLayer } = getWindow();
        if (process.env.NODE_ENV === 'development' && !_init && !dataLayer) {
            console.warn('There is no datalayer');
        }
        _init = true;
        return dataLayer;
    };
};
const getDataLayer = createGetDataLayer();
const TRACK_PAYLOAD_KEY = Symbol('track-payload-key');
const useInstanceInject = (key, instance, defaultValue) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = instance === null || instance === void 0 ? void 0 : instance.$) === null || _a === void 0 ? void 0 : _a.provides) === null || _b === void 0 ? void 0 : _b[key]) !== null && _c !== void 0 ? _c : defaultValue;
};
const useTackPayload = (instance) => {
    const defaultValue = {};
    if (instance) {
        return useInstanceInject(TRACK_PAYLOAD_KEY, instance, defaultValue);
    }
    return inject(TRACK_PAYLOAD_KEY, defaultValue);
};
const getEventName = (name) => {
    return `track.${name !== null && name !== void 0 ? name : 'unknown'}`;
};
const useTackPayloadWithInstance = () => {
    return useInstanceInject;
};
const useTrack = (event, _payload, instance) => {
    const injectedPayload = useTackPayload(instance);
    const mergedPayload = defaultsDeep(_payload !== null && _payload !== void 0 ? _payload : {}, injectedPayload);
    return (eventName, payload) => {
        var _a, _b;
        (_b = (_a = getDataLayer()) === null || _a === void 0 ? void 0 : _a.push) === null || _b === void 0 ? void 0 : _b.call(_a, Object.assign(Object.assign({}, defaultsDeep(payload, mergedPayload)), { event: getEventName(eventName) }));
    };
};
const provideTrackPayload = (payload = {}) => {
    const previousPayload = useTackPayload();
    provide(TRACK_PAYLOAD_KEY, Object.assign(Object.assign({}, previousPayload), payload));
};
const dropDirectiveBind = (element) => {
    const { __track } = element;
    if (__track) {
        const { eventName, handle } = __track;
        element.removeEventListener(eventName, handle);
    }
};
const directiveBind = (isInit = false) => {
    return (element, binding) => {
        if (!isInit) {
            dropDirectiveBind(element);
        }
        const { instance, arg: eventName = 'click', value: payload } = binding;
        const track = useTrack(eventName, payload, instance);
        const handle = (event) => {
            const { altKey, ctrlKey, target } = event !== null && event !== void 0 ? event : {};
            const { id, title, tagName, alt } = target !== null && target !== void 0 ? target : {};
            return track(eventName, { alt, altKey, ctrlKey, id, tagName, title });
        };
        element.addEventListener(eventName, handle);
        element.__track = {
            eventName,
            handle,
        };
    };
};
const trackDirective = {
    created: directiveBind(true),
    unmounted: dropDirectiveBind,
    updated: directiveBind(false),
};

const isToRef = (value) => {
    return Boolean(value === null || value === void 0 ? void 0 : value._key);
};

/**
 * Ref 여부와 상관 없이 값을 가져옵니다
 * @param value 값을 가져올 값 입니다
 */
const unwrapRef = (value) => {
    if (isRef(value)) {
        return value.value;
    }
    return value;
};

/**
 * Makes a ref value with a value, ref value, toRef value or computed value
 * @param value
 * @param options ref to WrapRefOptions
 */
const wrapRef = (value, options = {}) => {
    var _a, _b;
    const { bindValue = true, initState, defaultValue } = options;
    const valueRef = ref((_b = (_a = unwrapRef(value)) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : initState);
    const _isRef = isRef(value);
    const isReadonlyValue = !_isRef || isToRef(value) || isReadonly(value);
    if (_isRef) {
        watchEffect(() => {
            var _a, _b;
            valueRef.value = (_b = (_a = unwrapRef(value)) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : initState;
        }, {
            flush: 'sync',
        });
    }
    return computed({
        get: () => {
            return valueRef.value;
        },
        set: (_value) => {
            valueRef.value = _value;
            if (!bindValue || isReadonlyValue) {
                return;
            }
            value.value = _value;
        },
    });
};

/**
 * @deprecated please use onAnimationRepeater
 * @param handle
 * @param toggle
 */
const animationRepeater = (handle, toggle) => {
    const toggleRef = wrapRef(toggle, { initState: true });
    const cancelFlagRef = ref();
    const window = getWindow();
    const tick = () => {
        if (window && toggleRef.value) {
            handle();
            window.requestAnimationFrame(tick);
        }
    };
    const registerTick = (value) => {
        if (!window) {
            return;
        }
        const { requestAnimationFrame, cancelAnimationFrame } = window;
        if (value) {
            cancelFlagRef.value = requestAnimationFrame(tick);
            return;
        }
        if (cancelFlagRef.value) {
            cancelAnimationFrame(cancelFlagRef.value);
        }
    };
    watch(toggleRef, registerTick);
    if (toggleRef.value) {
        registerTick(true);
    }
    return toggleRef;
};
const onAnimationRepeater = animationRepeater;

const useBlur = () => {
    const document = getDocument();
    const HTMLElement = getHTMLElement();
    return () => {
        if (!document || !HTMLElement) {
            return;
        }
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    };
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function useElementEvent(element, eventName, listener, isActive, options = {}) {
    const { once = false, passive = true, capture = false, } = options;
    const instance = getCurrentInstance();
    const isInInstance = Boolean(instance);
    const elementRef = wrapRef(element, { bindValue: false });
    const isActiveRef = wrapRef(isActive, { initState: true });
    const handle = (event) => {
        listener(event);
        if (once) {
            inactive();
        }
    };
    const active = () => {
        const element = elementRef.value;
        if (element) {
            isActiveRef.value = true;
            element.addEventListener(eventName, handle, {
                capture,
                passive,
            });
        }
    };
    const inactive = () => {
        isActiveRef.value = false;
        const element = elementRef.value;
        if (element) {
            element.removeEventListener(eventName, handle);
        }
    };
    watch(elementRef, () => {
        if (isActiveRef.value) {
            inactive();
            active();
        }
    });
    watch(isActiveRef, (value) => {
        if (value) {
            active();
        }
        else {
            inactive();
        }
    });
    if (isActiveRef.value) {
        if (isInInstance) {
            onMounted(() => {
                active();
            });
        }
        else {
            active();
        }
    }
    if (isInInstance) {
        onUnmounted(() => {
            inactive();
        });
    }
    return isActiveRef;
}

let _legacyInput;
const getLegacyInput = () => {
    if (_legacyInput) {
        return _legacyInput;
    }
    const document = getDocument();
    if (!document) {
        return;
    }
    const _inputElement = document.querySelector('#__legacy_input__');
    if (_inputElement) {
        _legacyInput = _inputElement;
        return _legacyInput;
    }
    const inputElement = document.createElement('input');
    inputElement.id = '__legacy_input__';
    inputElement.style.display = 'block';
    inputElement.setAttribute('type', 'text');
    document.body.append(inputElement);
    _legacyInput = inputElement;
    return inputElement;
};
const blur = useBlur();
const legacyCopy = (value) => {
    const document = getDocument();
    const input = getLegacyInput();
    if (!input || !document) {
        return;
    }
    input.style.display = 'block';
    input.value = value;
    input.select();
    const result = document.execCommand('copy');
    if (!result) {
        return result;
    }
    blur();
    input.style.display = 'none';
    return result;
};
const useLegacyClipboard = (initState, updateOnEvent = false) => {
    const window = getWindow();
    const navigator = getNavigator();
    const clipboard = navigator === null || navigator === void 0 ? void 0 : navigator.clipboard;
    const valueRef = wrapRef(initState);
    const stateRef = ref('idle');
    const write = (value) => {
        stateRef.value = 'writing';
        const result = legacyCopy(value !== null && value !== void 0 ? value : valueRef.value);
        if (!result) {
            return Promise.reject(new Error('Cannot copy string (legacy)'));
        }
        if (value) {
            valueRef.value = value;
        }
        stateRef.value = 'idle';
        return Promise.resolve(valueRef.value);
    };
    const read = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!clipboard || stateRef.value !== 'idle') {
            return valueRef.value;
        }
        stateRef.value = 'reading';
        const value = yield clipboard.readText();
        valueRef.value = value;
        stateRef.value = 'idle';
        return value;
    });
    if (updateOnEvent && clipboard && window) {
        useElementEvent(window, 'copy', read);
        useElementEvent(window, 'cut', read);
    }
    return {
        read,
        value: valueRef,
        write,
    };
};

const useClipboard = (initState, updateOnEvent = false) => {
    const valueRef = wrapRef(initState);
    const navigator = getNavigator();
    const clipboard = navigator === null || navigator === void 0 ? void 0 : navigator.clipboard;
    const window = getWindow();
    const stateRef = ref('idle');
    const read = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!clipboard || stateRef.value !== 'idle') {
            return valueRef.value;
        }
        stateRef.value = 'reading';
        const value = yield clipboard.readText();
        valueRef.value = value;
        stateRef.value = 'idle';
        return value;
    });
    const write = (_value) => __awaiter(void 0, void 0, void 0, function* () {
        if (!clipboard || stateRef.value !== 'idle') {
            return valueRef.value;
        }
        const { value } = valueRef;
        const newValue = _value !== null && _value !== void 0 ? _value : value;
        if (newValue) {
            stateRef.value = 'writing';
            yield clipboard.writeText(newValue);
            stateRef.value = 'idle';
            valueRef.value = newValue;
        }
    });
    if (window && clipboard && updateOnEvent) {
        useElementEvent(window, 'copy', read);
        useElementEvent(window, 'cut', read);
    }
    return freeze({
        read,
        state: stateRef,
        value: valueRef,
        write,
    });
};

/**
 * @param handle
 */
const onOffline = (handle) => {
    const window = getWindow();
    return useElementEvent(window, 'offline', handle, true, { capture: false, passive: true });
};
/**
 * @param handle
 */
const onOnline = (handle) => {
    const window = getWindow();
    return useElementEvent(window, 'online', handle, true, { capture: false, passive: true });
};
const useConnection = (init = true) => {
    const value = wrapRef(init);
    onOnline(() => {
        value.value = true;
    });
    onOffline(() => {
        value.value = false;
    });
    return value;
};

const createProvide = (data, name) => {
    const key = Symbol(process$1.env.NODE_ENV === 'development' ? name : undefined);
    return freeze({
        inject: () => {
            return inject(key);
        },
        injectKey: key,
        provide: () => {
            const _data = mayFunctionValue(data);
            return provide(key, _data);
        },
    });
};

const defaultWait = 150;
const useDebounce = (handle, wait, immediate = false) => {
    const onHandle = (...args) => {
        return handle === null || handle === void 0 ? void 0 : handle(...args);
    };
    const waitRef = wrapRef(wait);
    const immediateRef = wrapRef(immediate);
    const onCall = computed(() => {
        const wait = waitRef.value;
        if (wait) {
            const _wait = typeof wait === 'number' ? wait : defaultWait;
            return debounce(onHandle, _wait, immediateRef.value);
        }
        return Object.assign(onHandle, {
            clear() {
                // empty
            },
        });
    });
    onBeforeUnmount(() => {
        var _a;
        (_a = onCall.value) === null || _a === void 0 ? void 0 : _a.clear();
    });
    return (...args) => {
        onCall.value(...args);
    };
};

const isElement = (value) => {
    return value instanceof Element;
};

const isComponentInstance = (value) => (Boolean(value === null || value === void 0 ? void 0 : value.$el));

const pickElement = (value) => {
    if (isComponentInstance(value)) {
        return value === null || value === void 0 ? void 0 : value.$el;
    }
    if (isElement(value)) {
        return value;
    }
};

const defaultThreshold$1 = 0.05;
const onElementIntersection = (element, handle, options = {}) => {
    if (isSSR()) {
        return;
    }
    const elementRef = wrapRef(element);
    const updateState = (entries, observer) => {
        handle === null || handle === void 0 ? void 0 : handle(entries, observer);
    };
    const observerRef = ref(new IntersectionObserver(updateState, Object.assign({ threshold: defaultThreshold$1 }, options)));
    watch(elementRef, (element) => {
        observerRef.value.disconnect();
        const _element = pickElement(element);
        if (_element) {
            // using any owing to typescript bug
            observerRef.value.observe(_element);
        }
    });
    onBeforeUnmount(() => {
        observerRef.value.disconnect();
    });
};
/**
 * @deprecated please use onElementIntersection
 */
const onIntersectionElement = onElementIntersection;

const defaultThreshold = 0.05;
const useElementIntersection = (element, options = {}) => {
    const { threshold = defaultThreshold, hide = true } = options, rest = __rest(options, ["threshold", "hide"]);
    const showRef = ref(false);
    onElementIntersection(element, (entries) => {
        if (hide) {
            const shouldClose = entries.some((entry) => {
                return entry.intersectionRatio < threshold;
            });
            if (shouldClose) {
                showRef.value = false;
                return;
            }
        }
        const shouldShow = entries.some((entry) => {
            return entry.intersectionRatio >= threshold;
        });
        if (shouldShow) {
            showRef.value = true;
        }
    }, Object.assign(Object.assign({}, rest), { threshold }));
    return showRef;
};

const historyRef = (value, max = 1, sources = []) => {
    const valueRef = wrapRef(value);
    const maxRef = wrapRef(max);
    const historyRef = wrapRef([]);
    watch(maxRef, () => {
        historyRef.value.splice(-1, historyRef.value.length - maxRef.value + 1);
    });
    watch([valueRef, ...sources], (_, [old]) => {
        historyRef.value.splice(-1, historyRef.value.length - maxRef.value + 1);
        historyRef.value.unshift(old);
    });
    return freeze(historyRef);
};

const isInInstance = () => {
    return Boolean(getCurrentInstance());
};

const toObjectProps = (props) => {
    if (Array.isArray(props)) {
        return Object.fromEntries(props.map((key) => {
            return [key, null];
        }));
    }
    return props;
};

const margeProps = (props, defaultProps) => {
    const _props = toObjectProps(props);
    if (!defaultProps) {
        return _props;
    }
    const _defaultProps = toObjectProps(defaultProps);
    return Object.assign(Object.assign({}, _defaultProps), _props);
};

const onClickOutside = (target, handle, options = {}) => {
    const { event = 'pointerdown' } = options;
    const targetRef = wrapRef(target);
    const elementRef = computed(() => {
        return pickElement(targetRef.value);
    });
    const listener = (event) => {
        const element = elementRef.value;
        if (!element) {
            return;
        }
        if (element === event.target || event.composedPath().includes(element)) {
            return;
        }
        handle(event);
    };
    return useElementEvent(isSSR() ? undefined : window, event, listener, true, { passive: true });
};

const onDomMounted = (handle) => {
    if (isSSR()) {
        return;
    }
    const isActive = useElementEvent(window, 'load', () => {
        handle();
        isActive.value = false;
    }, false);
    onMounted(() => {
        const isReady = document.readyState === 'complete';
        if (isReady) {
            handle();
            return;
        }
        isActive.value = true;
    });
};

/**
 * @deprecated
 * @param handle
 */
const onFocus = (handle) => {
    if (isSSR()) {
        return;
    }
    const onFocus = () => handle === null || handle === void 0 ? void 0 : handle();
    useElementEvent(window, 'focus', onFocus);
    useElementEvent(document, 'visibilitychange', onFocus);
};

const _isSSR = isSSR();
const original = _isSSR ? undefined : window.onpopstate;
const stateRef = ref$1();
const locationRef = ref$1(_isSSR ? undefined : document.location);
function onPopState(event) {
    if (typeof original === 'function') {
        original(event);
    }
    stateRef.value = event.state;
    locationRef.value = document.location;
}
if (!_isSSR) {
    window.onpopstate = onPopState;
}
/**
 * @WIP
 * @param callback
 */
const onRouteHistory = (callback) => {
    watch$1([stateRef, locationRef], ([state, location]) => {
        if (state && location) {
            callback(state, location);
        }
    });
};

const isVisible = () => {
    if (isSSR()) {
        return false;
    }
    return document.visibilityState !== 'hidden';
};
const defaultDebounceWait = 500;
/**
 * @experimental
 * @param handle
 * @param options
 */
const onShouldUpdate = (handle, options = {}) => {
    const { firstExecute = true, windowFocus = true, visibleElement, online = true, visibleDocument = true, debounce = defaultDebounceWait, watchValue, } = options;
    // noinspection SuspiciousTypeOfGuard
    const debounceWait = typeof debounce === 'boolean' ? defaultDebounceWait : debounce;
    const thisDocument = isSSR() ? null : window.document;
    const onHandle = () => {
        handle === null || handle === void 0 ? void 0 : handle();
    };
    const onDebounceHandle = useDebounce(onHandle, debounceWait, true);
    const onShouldUpdate = () => {
        if (debounce) {
            onDebounceHandle();
            return;
        }
        onHandle();
    };
    const onFocus = () => {
        if (windowFocus) {
            onShouldUpdate();
        }
    };
    const onVisibleDocument = (event) => {
        if (visibleDocument && event.target === thisDocument && isVisible()) {
            onShouldUpdate();
        }
    };
    const isOnline = useConnection(true);
    const isShowing = useElementIntersection(visibleElement);
    if (firstExecute && isVisible()) {
        onShouldUpdate();
    }
    watch(isShowing, (value) => {
        if (value) {
            onShouldUpdate();
        }
    });
    watch(isOnline, (value) => {
        if (online && value) {
            onShouldUpdate();
        }
    });
    watch(watchValue, () => {
        onShouldUpdate();
    });
    useElementEvent(document, 'visibilitychange', onVisibleDocument);
    useElementEvent(window, 'focus', onFocus);
    return onShouldUpdate;
};

/**
 * promise x ref
 * @deprecated please use usePromise2
 * @param recipe
 * @param options
 */
const usePromise = (recipe, options = {}) => {
    const { immediate, cleanOnExecute = true } = options;
    const dataRef = ref();
    const countRef = ref(0);
    const fetchingRef = ref(false);
    const errorRef = ref();
    const promiseRef = ref();
    const execute = (...args) => {
        if (cleanOnExecute) {
            dataRef.value = undefined;
        }
        fetchingRef.value = true;
        errorRef.value = undefined;
        countRef.value += 1;
        const promise = recipe(...args)
            .then((data) => {
            dataRef.value = data;
            fetchingRef.value = false;
            return data;
        })
            .catch((error) => {
            errorRef.value = error;
            fetchingRef.value = false;
            throw error;
        });
        promiseRef.value = promise;
        return promise;
    };
    if (Array.isArray(immediate)) {
        promiseRef.value = execute(...immediate);
    }
    return freeze({
        count: countRef,
        data: dataRef,
        error: errorRef,
        execute,
        fetching: fetchingRef,
        promise: promiseRef,
    });
};
const usePromise2 = (recipe, initData, options = {}) => {
    const { immediate, cleanOnExecute = true } = options;
    const dataRef = wrapRef(initData);
    const countRef = ref(0);
    const fetchingRef = ref(false);
    const errorRef = ref();
    const promiseRef = ref();
    const execute = (...args) => {
        if (cleanOnExecute) {
            dataRef.value = undefined;
        }
        const context = freeze({
            previousCount: countRef.value,
            previousData: dataRef.value,
            previousError: errorRef.value,
            previousFetching: fetchingRef.value,
            previousPromise: promiseRef.value,
        });
        fetchingRef.value = true;
        errorRef.value = undefined;
        countRef.value += 1;
        const promise = recipe(context, ...args)
            .then((data) => {
            dataRef.value = data;
            fetchingRef.value = false;
            return data;
        })
            .catch((error) => {
            errorRef.value = error;
            fetchingRef.value = false;
            throw error;
        });
        promiseRef.value = promise;
        return promise;
    };
    if (Array.isArray(immediate)) {
        promiseRef.value = execute(...immediate);
    }
    return freeze({
        count: countRef,
        data: dataRef,
        error: errorRef,
        execute,
        fetching: fetchingRef,
        promise: promiseRef,
    });
};

const getItem = (storage, key) => {
    let result;
    try {
        const raw = storage.getItem(key);
        if (raw) {
            result = JSON.parse(raw);
        }
    }
    catch (_a) {
        return;
    }
    return result;
};
const setItem = (storage, key, value) => {
    storage.setItem(key, stringify(value));
};
const storageRef = (key, value, options = {}) => {
    const { type = 'local' } = options;
    const valueRef = wrapRef(value);
    const freezeWatch = ref(false);
    const storage = getStorage(type);
    if (!storage) {
        return valueRef;
    }
    const updateStorage = (value) => {
        setItem(storage, key, value);
        valueRef.value = value;
    };
    const updateValue = (init, freeze = false) => {
        if (freeze) {
            freezeWatch.value = true;
        }
        const result = getItem(storage, key);
        if (typeof result !== 'undefined') {
            valueRef.value = result;
            return;
        }
        if (init) {
            updateStorage(init);
        }
    };
    updateValue(valueRef.value);
    useElementEvent(window, 'storage', () => {
        updateValue(undefined, true);
    });
    watch(valueRef, (value) => {
        if (freezeWatch.value) {
            freezeWatch.value = false;
            return;
        }
        setItem(storage, key, value);
    });
    return valueRef;
};

/**
 * toMutRef 는 vue 에 toRef 와 달리 readonly 가 아닙니다 리턴된 ref 값은 변경 가능합니다
 * @param props
 * @param key
 * @param options
 */
const toMutRef = (props, key, options = {}) => {
    const { shouldUpdate } = options;
    const valueRef = toRef(props, key);
    const valueMut = ref(valueRef.value);
    watch(valueRef, (value, oldValue) => {
        if (shouldUpdate) {
            if (shouldUpdate(value, oldValue)) {
                valueMut.value = value;
                return;
            }
            return;
        }
        valueMut.value = value;
    });
    return valueMut;
};

/**
 * @example
 * const valueRef = ref(false)
 * const {toggle, value} = toggleRef(valueRef)
 * @param value
 */
const toggleRef = (value = false) => {
    const valueRef = wrapRef(value);
    const toggle = () => {
        valueRef.value = !valueRef.value;
    };
    return freeze({
        toggle,
        value: valueRef,
    });
};

const unWrapRefs = flow((record) => Object.keys(record).map((key) => {
    const value = record[key];
    return [key, unwrapRef(value)];
}), (entries) => Object.fromEntries(entries));

export { TRACK_PAYLOAD_KEY, animationRepeater, createGetDataLayer, createProvide, directiveBind, dropDirectiveBind, getDataLayer, getEventName, historyRef, isComponentInstance, isElement, isInInstance, isToRef, isVisible, margeProps, onAnimationRepeater, onClickOutside, onDomMounted, onElementIntersection, onFocus, onIntersectionElement, onOffline, onOnline, onRouteHistory, onShouldUpdate, pickElement, provideTrackPayload, storageRef, toMutRef, toObjectProps, toggleRef, trackDirective, unWrapRefs, unwrapRef, useBlur, useClipboard, useConnection, useDebounce, useElementEvent, useElementIntersection, useInstanceInject, useLegacyClipboard, usePromise, usePromise2, useTackPayload, useTackPayloadWithInstance, useTrack, wrapRef };
